import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProductsTable from '../../component/productsComps/ProductsTable';
import api from '../../utils/api';
import MasterHeader from '../../component/reusable/MasterHeader';
import { clearProductSelection, getAllProductsThunk, setBulkUploadData, setProductPage, setProductRefresh, deletemultipleProductsThunk } from '../../redux/productSlice';
import { setManufacturerRefresh } from '../../redux/manufacturer/getAllManufacturerSlice';
import * as XLSX from 'xlsx';
import { parseExcelFileLocally } from '../../utils/excelProductParser';
import { downloadCatalogProductsExcel } from '../../utils/quickListHelpers';



import DeleteModal from '../../component/reusable/deleteModal/DeleteModal';

// Products fetched per request while exporting.
const EXPORT_PAGE_SIZE = 500;

const AllProduct = () => {

  const { products, loading, error, refresh, page, selectedIds, allProductsSelected, allProductsFilters } = useSelector((state) => state.products);
  const { user } = useSelector((state) => state.user);
  const [search, setSearch] = useState('')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { categoryId } = useParams()
  const categoryNameFromUrl = new URLSearchParams(location.search).get('categoryName') || '';
  const [categoryName, setCategoryName] = useState(location.state?.categoryName || categoryNameFromUrl);

  const [statuss, setStatus] = useState("");
  // const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [sortBy, setSortyBy] = useState('createdAt');
  const [order, setOrder] = useState('asc')
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [isDeleteSelectedModalOpen, setIsDeleteSelectedModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const product = products?.data
  const totalPages = products?.totalPages
  const totalItems = products?.totalItems
  const itemsPerPage = products?.limit
  const selectedCount = allProductsSelected ? (Number(totalItems) || 0) : selectedIds.length;
  const categoryQuery = categoryId ? `&categoryId=${encodeURIComponent(categoryId)}` : '';
  const productQuery = `?search=${search}&page=${page}&status=${statuss}&limit=${limit}&sortBy=${sortBy}&order=${order}${categoryQuery}`;

  // calculate start & end of items -------------------
  const start = (page - 1) * limit + 1;
  const end =
    totalPages === page
      ? totalItems
      : (page - 1) * limit + limit;


  useEffect(() => {
    dispatch(getAllProductsThunk(productQuery));
  }, [dispatch, productQuery, refresh]);

  useEffect(() => {
    dispatch(clearProductSelection());
  }, [dispatch, search, statuss, categoryId]);

  useEffect(() => {
    if (!categoryId) {
      setCategoryName('');
      return;
    }

    const nameFromNavigation = location.state?.categoryName || categoryNameFromUrl;
    if (nameFromNavigation) {
      setCategoryName(nameFromNavigation);
      return;
    }

    api.viewCategory(categoryId)
      .then((response) => setCategoryName(response?.data?.data?.name || ''))
      .catch(() => setCategoryName(''));
  }, [categoryId, location.state?.categoryName, categoryNameFromUrl]);

  const handleFileChange = async (e) => {
    let progressToastId = null;
    try {
      const file = e.target.files[0];
      if (!file) return;

      e.target.value = '';

      if (uploadingFile) return;
      setUploadingFile(true);
      progressToastId = toast.loading('Reading and parsing Excel file...');

      let parsedProducts = [];

      try {
        // Fast client-side parsing using XLSX:
        // Reads in-memory in browser, completely avoids Nginx 413 (Request Entity Too Large),
        // and supports files of any size with no limits.
        parsedProducts = await parseExcelFileLocally(file);
      } catch (localParseErr) {
        console.warn('Local parsing encountered an issue, trying backend upload fallback:', localParseErr);
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.bulkUploadProduct(formData);
        parsedProducts = response?.data?.data || [];
      }

      if (Array.isArray(parsedProducts) && parsedProducts.length > 0) {
        toast.update(progressToastId, {
          render: `${parsedProducts.length} products loaded successfully.`,
          type: 'success',
          isLoading: false,
          autoClose: 2000
        });
        dispatch(setBulkUploadData(parsedProducts));
        navigate('/products/bulkProdUpload');
      } else {
        toast.update(progressToastId, {
          render: 'No products were found in the uploaded file.',
          type: 'error',
          isLoading: false,
          autoClose: 5000
        });
      }
    } catch (err) {
      console.log(err);
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      if (progressToastId) {
        toast.update(progressToastId, { render: message, type: 'error', isLoading: false, autoClose: 5000 });
      } else {
        toast.error(message);
      }
    } finally {
      setUploadingFile(false);
    }
  };

  const handleOpenMenu = (sort) => {
    setSortyBy(sort);
    setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  // Exports exactly what this page lists (its category, search and status
  // filters; only the ticked products when some are selected) as an Excel
  // file in the bulk-upload format, so it can be edited and uploaded again.
  const exportProducts = async () => {
    const listed = Number(totalItems) || 0;
    if (!categoryId && listed > 20000 && !window.confirm(`Export all ${listed} products? This can take a few minutes.`)) return;

    setExporting(true);
    const toastId = toast.loading('Preparing Excel…');
    try {
      const baseQuery = `?search=${encodeURIComponent(search)}&status=${statuss}&sortBy=${sortBy}&order=${order}${categoryQuery}`;
      const all = [];
      let pageNo = 1;
      let pages = 1;
      do {
        const res = await api.getAllProduct(`${baseQuery}&page=${pageNo}&limit=${EXPORT_PAGE_SIZE}`);
        all.push(...(res?.data?.data || []));
        pages = Number(res?.data?.totalPages) || 1;
        toast.update(toastId, { render: `Preparing Excel… ${all.length} / ${res?.data?.totalItems ?? all.length}` });
        pageNo += 1;
      } while (pageNo <= pages);

      let chosen = all;
      if (!allProductsSelected && selectedIds.length) {
        const wanted = new Set(selectedIds.map(String));
        chosen = all.filter((p) => wanted.has(String(p._id)));
      }
      if (!chosen.length) {
        toast.update(toastId, { render: 'No products to export', type: 'warning', isLoading: false, autoClose: 3000 });
        return;
      }

      const label = categoryId ? categoryName || 'category' : 'all';
      const fileLabel = label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'products';
      downloadCatalogProductsExcel(chosen, `${fileLabel}-products-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.update(toastId, { render: `Exported ${chosen.length} product(s) to Excel`, type: 'success', isLoading: false, autoClose: 3000 });
    } catch (err) {
      toast.update(toastId, {
        render: err?.response?.data?.message || err?.message || 'Product export failed',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const isSuperAdmin = () => {
    const role = user?.type || user?.role || user?.user?.type || user?.user?.role;
    return role && role.toString().toUpperCase() === 'SUPERADMIN';
  };

  const handleDeleteAllClick = () => {
    if (!isSuperAdmin()) {
      toast.error("Only Super Admin is authorized to delete products");
      return;
    }
    if (selectedCount > 0) {
      setIsDeleteSelectedModalOpen(true);
    } else {
      setIsDeleteAllModalOpen(true);
    }
  };

  const deleteSelected = async (password) => {
    try {
      setDeleteLoading(true);
      const data = { ids: selectedIds, password, selectAll: allProductsSelected, filters: allProductsFilters };
      await dispatch(deletemultipleProductsThunk(data));
      setIsDeleteSelectedModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteAll = async (password) => {
    try {
      setDeleteLoading(true)
      const { status } = await api.deleteAllProduct({ password });

      if (status === 200) {
        toast.success("All products deleted");
        dispatch(setProductRefresh());
        setIsDeleteAllModalOpen(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong")
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <div>

      <MasterHeader
        index={categoryId ? `${categoryName || 'Level 3'} Products` : 'Product'}
        root="Dashboard"
        setStatus={setStatus}
        setSearch={setSearch}
        deleteAll={handleDeleteAllClick}
        deleteLoading={deleteLoading}
        handleFileChange={handleFileChange}
        onClickadd={() => navigate('/products/addProduct')}
        onQuickList={() => navigate('/products/quickList')}
        buttonText={'Add Product'}
        exportData={exportProducts}
        exportLoading={exporting}
        hideStatus={true}
      />
      {categoryId && !loading && (
        <div style={{ margin: '0 0 12px', fontWeight: 600, color: '#343a40' }}>
          Total products: {Number(totalItems) || 0}{categoryName ? ` in ${categoryName}` : ''}
        </div>
      )}
      {loading ?
        <div>
          Please wait...
        </div>
        :
        <>
          {
            product?.length > 0 ?
              <ProductsTable
                product={product}
                limit={limit}
                page={page}
                setPage={setProductPage}
                itemsPerPage={itemsPerPage}
                start={start}
                end={end}
                totalItems={totalItems}
                handleOpenMenu={handleOpenMenu}
                selectionFilters={{ search, status: statuss, categoryId }}
                onBrowseCategories={() => {
                  dispatch(clearProductSelection());
                  navigate('/products/categories');
                }}
              />
              :
              <div>
                No product found
              </div>
          }
        </>
      }
      <DeleteModal
        isOpen={isDeleteSelectedModalOpen}
        title="Delete Selected Products"
        description={`Are you sure you want to delete the ${selectedCount} selected product(s)?`}
        onClose={() => setIsDeleteSelectedModalOpen(false)}
        onDelete={deleteSelected}
        loading={deleteLoading}
      />
      <DeleteModal
        isOpen={isDeleteAllModalOpen}
        title="Delete All Products"
        description="No products selected. Are you sure you want to delete all products in the database?"
        onClose={() => setIsDeleteAllModalOpen(false)}
        onDelete={deleteAll}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AllProduct;
